import noop from "lodash/noop";
import { createContext } from "react";

import type { UserSong } from "db/schemas/userSong";
import type { PlayerState, ShuffleMode } from "features/player/types";
import type { PlayedSongRequestData } from "services/api";

/**
 * PlaybackPositionContext
 *
 * Only contains position and duration information, and methods directly related
 * to updating position. Components like progress bars and time displays should
 * use this context.
 */
export interface PlaybackPositionContextType {
  position: number;
  duration: number;
  progressUpdated: (position: number, duration: number) => void;
  songSeeked: (position: number) => void;
}

export const PlaybackPositionContext =
  createContext<PlaybackPositionContextType>({
    position: 0,
    duration: 0,
    progressUpdated: noop,
    songSeeked: noop,
  });

/**
 * PlayerStateContext
 *
 * Contains information about the current state of the player.
 * Components like song entries, player UI should use this context.
 */
export interface PlayerStateContextType {
  currentSong?: UserSong;
  playerState: PlayerState;
  playlist: Array<UserSong>;
  shuffleMode: ShuffleMode;
  muted: boolean;
  volume: number;
}

export const PlayerStateContext = createContext<PlayerStateContextType>({
  currentSong: undefined,
  playerState: "paused",
  playlist: [],
  shuffleMode: "off",
  muted: false,
  volume: 0,
});

/**
 * PlayerActionsContext
 *
 * Contains all methods to interact with the player.
 * Components like control buttons should use this context.
 */
export interface PlayerActionsContextType {
  currentSongUpdated: (song: UserSong) => void;
  muteToggled: () => void;
  playToggled: () => void;
  shuffleModeToggled: () => void;
  songAddedToLibrary: (songId: string) => void;
  songDownloaded: (songId: string) => void;
  songDisliked: (songId: string) => void;
  songLiked: (songId: string) => void;
  songPlayed: (stats: PlayedSongRequestData) => void;
  songShared: (songId: string) => void;
  songSkipped: () => void;
  volumeUpdated: (volume: number) => void;
}

export const PlayerActionsContext = createContext<PlayerActionsContextType>({
  currentSongUpdated: noop,
  muteToggled: noop,
  playToggled: noop,
  shuffleModeToggled: noop,
  songAddedToLibrary: noop,
  songDownloaded: noop,
  songDisliked: noop,
  songLiked: noop,
  songPlayed: noop,
  songShared: noop,
  songSkipped: noop,
  volumeUpdated: noop,
});
